import React, { Component, ComponentType } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router";
import { GarageKeyType } from "src/components/UserAccount/shared/GarageKey";
import { authenticationMergeRequest } from "src/store/actions/deal";
import { AccountState, GarageRequestAccess, VehicleState } from "src/store/types/state";
import { requestAccessCode } from "../AccessGarage/data";
import { verifyAccessCode } from "./data";
import { validateUnlockCode } from "./lib";
import UnlockGarage, { UnlockGarageProps } from "./UnlockGarage";

interface GarageUnlockStateProps extends RouteComponentProps {
    accessRequest: GarageRequestAccess | undefined;
    account: AccountState;
    environment: string;
    getVehicles: (uuid: string) => void;
    updateAccessRequest: (request: GarageRequestAccess | null) => void;
    uuid: string | undefined;
    vehicles: VehicleState[];
    isLockedPricing: boolean;
    onSuccessfulAuthentication: typeof authenticationMergeRequest;
}

interface GarageUnlockState {
    code: string[];
    hasError: boolean;
    message: string;
    garageKey: string;
    garageKeyType: GarageKeyType;
    isNew: boolean;
    isFromAccessMyGarage: boolean;
}

const UnlockGarageState = (WrappedComponent: ComponentType<UnlockGarageProps>) => {
    class UnlockGarageComponent extends Component<GarageUnlockStateProps & WithTranslation, GarageUnlockState> {
        constructor(props: GarageUnlockStateProps & WithTranslation) {
            super(props);
            this.state = {
                code: [],
                garageKey: (props.accessRequest && props.accessRequest.garageKey) || "",
                garageKeyType: (props.accessRequest && props.accessRequest.garageKeyType) || GarageKeyType.phone,
                hasError: false,
                isNew: !!(props.accessRequest && props.accessRequest.isNew),
                message: "",
                isFromAccessMyGarage: (this.props.location.state as { from: string }).from === "/access-my-garage",
            };
        }

        public render() {
            const props = {
                ...this.state,
                garageKey: this.state.garageKey,
                onSuccess: this.onSuccess,
                resendCode: this.resendCode,
                resetCode: this.resetCode,
                setCodeByIndex: this.setCodeByIndex,
                submitUnlock: this.submitUnlock,
            } as UnlockGarageProps;
            return <WrappedComponent {...props} />;
        }

        private setCodeByIndex = (index: number, value: string) => {
            this.setState(({ code }) => {
                const newCode = [...code];
                newCode[index] = value;
                return {
                    code: newCode,
                    hasError: false,
                    message: "",
                };
            });
        };

        private resetCode = (value: string) => {
            this.setState({
                code: [...value],
                hasError: false,
                message: "",
            });
        };

        private onSuccess = () => {
            this.props.history.push("/", { fromUnlock: true });
        };

        private submitUnlock = async () => {
            const code = this.state.code.join("");
            const validation = validateUnlockCode(code);

            if (!validation.isValid) {
                this.setState({
                    hasError: true,
                    message: validation.errorMessage,
                });
                return false;
            }

            try {
                const { uuid: verifiedUuid } = await verifyAccessCode(
                    this.props.account.slug,
                    code,
                    this.state.garageKey,
                    this.state.garageKeyType,
                    this.state.isFromAccessMyGarage
                );

                this.props.onSuccessfulAuthentication(
                    {
                        uuid: this.props.uuid!,
                        verifiedUuid,
                        garageKeyType: this.state.garageKeyType === GarageKeyType.email ? "email" : "sms",
                        vins: this.props.vehicles.map((vehicle) => vehicle.vin),
                    },
                    {
                        environment: this.props.environment,
                        slug: this.props.account.slug,
                    },
                    this.state.isFromAccessMyGarage || !this.props.isLockedPricing || this.props.uuid !== verifiedUuid
                );

                this.setState({
                    hasError: false,
                    message: "",
                });

                return true;
            } catch (e) {
                this.setState({
                    hasError: true,
                    message: this.props.t("UserAccount.UnlockGarage.incorrectCodeMessage"),
                });

                return false;
            }
        };

        private resendCode = async () => {
            const { account, updateAccessRequest, t } = this.props;
            const { garageKeyType, garageKey, isNew } = this.state;

            try {
                await requestAccessCode(garageKeyType, account.slug, garageKey);

                updateAccessRequest({
                    garageKey,
                    garageKeyType,
                    isNew,
                });

                this.setState({
                    hasError: false,
                    message: t("UserAccount.UnlockGarage.resendOkMessage"),
                });
                return true;
            } catch (e) {
                console.error("Resend Code error", e);
                this.setState({
                    hasError: true,
                    message: t("UserAccount.UnlockGarage.resendErrorMessage"),
                });
                return false;
            }
        };
    }

    return withTranslation()(UnlockGarageComponent);
};
export default UnlockGarageState(UnlockGarage);
